/**
 * iter267 — Bibliothèque photos de l'atelier.
 * Prise de photo (appareil) ou import depuis l'appareil, véhicule choisi à la
 * main, classement par date uniquement. Les photos jamais rattachées à un
 * dossier sont supprimées automatiquement au bout de 30 jours.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, Upload, Loader2, Car, Search, X, Trash2, Link2, ImageIcon, ScanText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { photoCompress } from "@/components/or/photoCompress";
import CarteGriseOrDialog from "@/components/photos/CarteGriseOrDialog";
import CarteGriseAdjustDialog from "@/components/photos/CarteGriseAdjustDialog";
import { photoDataUrl } from "@/components/photos/photoDataUrl";

const fmtJour = (iso) => {
  if (!iso) return "Sans date";
  return new Date(iso).toLocaleDateString("fr-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
};

export default function PhotoLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [vehicule, setVehicule] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [apercu, setApercu] = useState(null);
  const [filtreVehicule, setFiltreVehicule] = useState(null);
  // iter280 — lecture d'une carte grise → création d'un OR
  const [analyse, setAnalyse] = useState(null);
  const [analysePhotoId, setAnalysePhotoId] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  // iter282 — cadrage / rotation avant la lecture
  const [ajustSrc, setAjustSrc] = useState(null);

  const preparerCarteGrise = async (pid) => {
    setAnalysing(true);
    try {
      setAnalysePhotoId(pid);
      setAjustSrc(await photoDataUrl(pid));
      setApercu(null);
    } catch {
      toast.error("Photo illisible");
    } finally {
      setAnalysing(false);
    }
  };

  const analyserCarteGrise = async (image) => {
    setAnalysing(true);
    try {
      const { data } = await api.post(`/photos/${analysePhotoId}/carte-grise/analyser`, { image });
      setAjustSrc(null);
      setAnalyse(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lecture de la carte grise impossible");
    } finally {
      setAnalysing(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/photos", {
        params: filtreVehicule ? { vehicule_id: filtreVehicule.id } : {},
      });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [filtreVehicule]);

  useEffect(() => { load(); }, [load]);

  const envoyer = async (files) => {
    const liste = Array.from(files || []);
    if (!liste.length) return;
    setUploading(true);
    try {
      const photos = [];
      for (const f of liste) photos.push(await photoCompress(f, 600));
      await api.post("/photos", { photos, vehicule_id: vehicule?.id || null });
      toast.success(`${photos.length} photo(s) ajoutée(s)${vehicule ? ` · ${vehicule.plaque}` : " · véhicule à préciser"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setUploading(false);
    }
  };

  const affecter = async (pid, veh) => {
    try {
      await api.patch(`/photos/${pid}`, { vehicule_id: veh?.id || null });
      toast.success(veh ? `Photo rattachée à ${veh.plaque}` : "Véhicule retiré");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modification impossible");
    }
  };

  const supprimer = async (pid) => {
    try {
      await api.delete(`/photos/${pid}`);
      setApercu(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    }
  };

  const groupes = items.reduce((acc, p) => {
    const jour = (p.created_at || "").slice(0, 10);
    (acc[jour] = acc[jour] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="photo-library-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-6 h-6 text-[#52a675]" /> Photos
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Prenez vos photos ici, choisissez le véhicule, puis réutilisez-les dans un OR.
          </p>
        </div>
      </div>

      <Card className="p-4 border-gray-200 mt-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPickerOpen(true)}
            className="h-10 border-gray-300"
            data-testid="photo-vehicule-btn"
          >
            <Car className="w-4 h-4 mr-2 text-[#52a675]" />
            {vehicule ? `${vehicule.plaque} · ${vehicule.marque} ${vehicule.modele}` : "Choisir le véhicule"}
          </Button>
          {vehicule && (
            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setVehicule(null)} data-testid="photo-vehicule-clear">
              <X className="w-4 h-4" />
            </Button>
          )}

          <label className="inline-flex" data-testid="photo-capture-label">
            <Button asChild className="h-10 bg-[#52a675] hover:bg-[#3d8a5c] pointer-events-none">
              <span><Camera className="w-4 h-4 mr-2" /> Prendre une photo</span>
            </Button>
            <input
              type="file" multiple accept="image/*" capture="environment" className="hidden"
              onChange={(e) => envoyer(e.target.files)}
              data-testid="photo-capture-input"
            />
          </label>

          <label className="inline-flex" data-testid="photo-import-label">
            <Button asChild variant="outline" className="h-10 border-gray-300 pointer-events-none">
              <span><Upload className="w-4 h-4 mr-2" /> Importer</span>
            </Button>
            <input
              type="file" multiple accept="image/*" className="hidden"
              onChange={(e) => envoyer(e.target.files)}
              data-testid="photo-import-input"
            />
          </label>

          {uploading && <Loader2 className="w-4 h-4 animate-spin text-[#52a675]" data-testid="photo-uploading" />}

          <div className="ml-auto flex items-center gap-2">
            {filtreVehicule ? (
              <Badge
                variant="outline"
                className="h-10 px-3 bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/40 cursor-pointer"
                onClick={() => setFiltreVehicule(null)}
                data-testid="photo-filtre-actif"
              >
                Filtre : {filtreVehicule.plaque} <X className="w-3 h-3 ml-1" />
              </Badge>
            ) : (
              <span className="text-xs text-gray-400">Les photos non rattachées sont effacées après 30 jours.</span>
            )}
          </div>
        </div>
        {!vehicule && (
          <p className="text-[11px] text-gray-400 mt-2">
            Sans véhicule sélectionné, les photos restent à classer : vous pourrez le préciser ensuite sur chaque photo.
          </p>
        )}
      </Card>

      {loading ? (
        <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : !items.length ? (
        <Card className="p-12 text-center border-dashed border-2 border-gray-200" data-testid="photo-empty">
          <Camera className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="font-heading font-semibold text-lg text-gray-700">Aucune photo pour l&apos;instant</h3>
          <p className="text-sm text-gray-500 mt-1">Choisissez un véhicule puis prenez vos photos.</p>
        </Card>
      ) : (
        Object.entries(groupes).sort((a, b) => b[0].localeCompare(a[0])).map(([jour, photos]) => (
          <div key={jour} className="mb-8" data-testid={`photo-groupe-${jour}`}>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-base font-semibold text-gray-800 capitalize">{fmtJour(jour)}</h2>
              <span className="text-xs text-gray-400">{photos.length} photo{photos.length > 1 ? "s" : ""}</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {photos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setApercu(p)}
                  className="group relative text-left rounded-lg overflow-hidden border border-gray-200 hover:border-[#52a675] hover:shadow-md transition-all"
                  data-testid={`photo-item-${p.id}`}
                >
                  <img src={p.thumb} alt="" className="w-full h-32 object-cover" />
                  <div className="p-2">
                    <div className="text-[11px] font-semibold text-gray-800 truncate">
                      {p.plaque || "À classer"}
                    </div>
                    <div className="text-[10px] text-gray-400 truncate">
                      {new Date(p.created_at).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}
                      {p.created_by_nom ? ` · ${p.created_by_nom}` : ""}
                    </div>
                  </div>
                  {(p.rattachements || []).length > 0 && (
                    <span
                      className="absolute top-1 left-1 inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-[#52a675] rounded-full px-1.5 py-0.5"
                      title={`Utilisée dans : ${(p.rattachements || []).map((r) => r.numero).join(", ")}`}
                      data-testid={`photo-item-${p.id}-rattachee`}
                    >
                      <Link2 className="w-2.5 h-2.5" /> {(p.rattachements || []).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))
      )}

      <VehiculePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(v) => { setVehicule(v); setPickerOpen(false); }}
      />

      <PhotoApercuDialog
        photo={apercu}
        onClose={() => setApercu(null)}
        onAffecter={affecter}
        onSupprimer={supprimer}
        onFiltrer={(v) => { setFiltreVehicule(v); setApercu(null); }}
        onCarteGrise={preparerCarteGrise}
        analysing={analysing}
      />

      <CarteGriseAdjustDialog
        open={!!ajustSrc}
        src={ajustSrc}
        busy={analysing}
        onCancel={() => setAjustSrc(null)}
        onValidate={analyserCarteGrise}
      />

      <CarteGriseOrDialog
        open={!!analyse}
        photoId={analysePhotoId}
        analyse={analyse}
        onClose={() => { setAnalyse(null); load(); }}
      />
    </div>
  );
}

const VehiculePickerDialog = ({ open, onClose, onPick }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/search", { params: { q } });
        setRes(data.vehicules || []);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="photo-vehicule-dialog">
        <DialogHeader>
          <DialogTitle>Choisir le véhicule</DialogTitle>
          <DialogDescription>Recherchez par plaque, marque ou modèle.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Ex : GE 123 456, Golf…" className="pl-10 h-10"
            data-testid="photo-vehicule-search"
          />
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
          {busy && <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>}
          {res.map((v) => (
            <button
              key={v.id}
              onClick={() => onPick(v)}
              className="w-full text-left py-2.5 px-1 hover:bg-gray-50 transition-colors"
              data-testid={`photo-vehicule-option-${v.id}`}
            >
              <div className="text-sm font-semibold text-gray-900">{v.plaque} · {v.marque} {v.modele}</div>
              <div className="text-xs text-gray-500">{v.client_nom}</div>
            </button>
          ))}
          {!busy && q.length >= 2 && !res.length && (
            <div className="py-6 text-center text-sm text-gray-400">Aucun véhicule trouvé</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PhotoApercuDialog = ({ photo, onClose, onAffecter, onSupprimer, onFiltrer, onCarteGrise, analysing }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!photo) return null;
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/photos/${photo.id}/file`;
  return (
    <>
      <Dialog open={!!photo} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl" data-testid="photo-apercu-dialog">
          <DialogHeader>
            <DialogTitle>{photo.vehicule_label || "Photo à classer"}</DialogTitle>
            <DialogDescription>
              {new Date(photo.created_at).toLocaleString("fr-CH")} · {Math.round((photo.taille || 0) / 1024)} KB
            </DialogDescription>
          </DialogHeader>
          <img src={photo.thumb} alt="" className="w-full max-h-[50vh] object-contain rounded-lg bg-gray-50" />
          <div className="rounded-lg border border-[#52a675]/40 bg-[#52a675]/5 p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-[#2d5f3f]">
              <span className="font-semibold">C&apos;est une carte grise ?</span> Je la lis (CH, FR, DE, IT, AT),
              je retrouve ou crée le client et le véhicule, puis j&apos;ouvre l&apos;OR.
            </div>
            <Button
              size="sm"
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              disabled={analysing}
              onClick={() => onCarteGrise(photo.id)}
              data-testid="photo-apercu-carte-grise"
            >
              {analysing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanText className="w-4 h-4 mr-2" />}
              Créer un OR
            </Button>
          </div>
          {(photo.rattachements || []).length > 0 && (
            <div className="text-xs text-[#2d5f3f]" data-testid="photo-apercu-rattachements">
              Utilisée dans : {(photo.rattachements || []).map((r) => r.numero).join(", ")}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)} data-testid="photo-apercu-affecter">
              <Car className="w-4 h-4 mr-2" /> {photo.vehicule_id ? "Changer de véhicule" : "Affecter un véhicule"}
            </Button>
            {photo.vehicule_id && (
              <Button variant="outline" size="sm" onClick={() => onFiltrer({ id: photo.vehicule_id, plaque: photo.plaque })} data-testid="photo-apercu-filtrer">
                Voir toutes ses photos
              </Button>
            )}
            <a href={url} target="_blank" rel="noreferrer" className="text-xs text-gray-500 underline" data-testid="photo-apercu-full">
              Ouvrir en pleine taille
            </a>
            <Button
              variant="outline" size="sm"
              className="ml-auto border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => onSupprimer(photo.id)}
              data-testid="photo-apercu-supprimer"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <VehiculePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(v) => { setPickerOpen(false); onAffecter(photo.id, v); onClose(); }}
      />
    </>
  );
};
